import ExitModal from 'components/common/modals/ExitModal';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPageName } from 'src/components/common/analyticsUtils/analyticsUtils';
import { StepFooter } from 'src/features/transfer/components/StepFooter/StepFooter';
import StepHeader from 'src/features/transfer/components/StepHeader/StepHeader';
import { getTransferFlowSteps, TransferFlowStep } from 'src/features/transfer/steps';
import {
  selectAccountType,
  selectLastStep,
  selectSelectedInternalAccount,
} from 'src/features/transfer/transferSlice';
import { selectIsCustomerWebView } from 'src/state/authSlice/authSlice';
import { RootState } from 'src/state/store';
import { dispatchPageLoad } from 'utils/analyticsUtil';
import { TransferFlow as TransferFlowEnum } from 'utils/constants';
import {
  useSendManualSalesforceRequestPayload,
  useSendManualTransferPayload,
  useSendYodleeManualTransferPayload,
  useSendYodleeSalesforceRequestPayload,
  useSendYodleeTransferPayload,
} from '../../features/transfer/hooks/useSendAssetTransferPayload';
import './TransferFlow.scss';

const TransferFlow: React.FC = () => {
  const currentStepNum = useSelector((state: RootState) => state.transfer.currentStep);
  const dispatch = useDispatch();
  const [isStepValid, setIsStepValid] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [isAccountAvailable, setIsAccountAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const transferFlow = useSelector((state: RootState) => state.transfer.transferFlow);
  const accountType = useSelector(selectAccountType) as number | null;
  const manualExternalAccount = useSelector(
    (state: RootState) => state.transfer.manualExternalAccount,
  );
  const manualAccountType = manualExternalAccount.accountType ?? null;
  const providername = manualExternalAccount.institutionName ?? null;
  const yodleeproviderID = useSelector(
    (state: RootState) => state.yodleeConfig.providerAccountID,
  ) as string | null;
  const selectedProvider =
    transferFlow === TransferFlowEnum.Automated ||
    transferFlow === TransferFlowEnum.AutomatedIncomplete
      ? yodleeproviderID
      : providername;
  const selectedAccountTypeId =
    transferFlow === TransferFlowEnum.Automated ||
    transferFlow === TransferFlowEnum.AutomatedIncomplete
      ? accountType
      : manualAccountType;
  const isCustomerWebView = useSelector(selectIsCustomerWebView) ?? false;

  const transferState = {
    cancelUrl: useSelector((state: RootState) => state.appConfig.cancelUrl),
    nasuUrl: useSelector((state: RootState) => state.appConfig.nasuUrl),
    docusignUrl: useSelector((state: RootState) => state.transfer.docusignUrl),
    transferType: useSelector((state: RootState) => state.transfer.transferType),
    transferFlow: transferFlow,
    stepState: { isStepValid, setIsStepValid },
    showErrorNotification: { showValidation, setShowValidation },
    showErrorModal: {
      showValidationModal,
      setShowValidationModal,
      isAccountAvailable,
      setIsAccountAvailable,
    },
    showExitModal: { showExitModal, setShowExitModal },
    lastStep: useSelector(selectLastStep) as number,
    isOpeningNewInternalAccount:
      useSelector(selectSelectedInternalAccount)?.selectedId === -1,
    selectedAccountTypeId: selectedAccountTypeId,
    isCustomerWebView,
  };

  const sendYodleeTransferPayload = useSendYodleeTransferPayload();
  const sendManualTransferPayload = useSendManualTransferPayload();
  const sendYodleeManualTransferPayload = useSendYodleeManualTransferPayload();
  const sendYodleeSalesforceTransferPayload = useSendYodleeSalesforceRequestPayload();
  const sendManualSalesforceRequestPayload = useSendManualSalesforceRequestPayload();

  const sendAssetTransferPayload = async () => {
    setIsLoading(true);

    try {
      let transferPayloadFunction;

      switch (transferState.transferFlow) {
        case TransferFlowEnum.Automated:
          transferPayloadFunction = sendYodleeTransferPayload;
          break;
        case TransferFlowEnum.AutomatedIncomplete:
          transferPayloadFunction = sendYodleeManualTransferPayload;
          break;
        default:
          transferPayloadFunction = sendManualTransferPayload;
      }

      await transferPayloadFunction();
    } catch (error) {
      console.error('Error sending transfer payload:', error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const currentpage = getPageName(currentStepNum);
    dispatchPageLoad(currentpage);
  }, [currentStepNum]);

  useEffect(() => {
    const handleUnload = () => {
      if (transferState.transferFlow === TransferFlowEnum.Manual) {
        sendManualSalesforceRequestPayload();
      } else if (
        transferState.transferFlow === TransferFlowEnum.Automated ||
        transferState.transferFlow === TransferFlowEnum.AutomatedIncomplete
      ) {
        sendYodleeSalesforceTransferPayload();
      }
    };
    if (
      selectedProvider &&
      !isCustomerWebView &&
      currentStepNum >= 4 &&
      currentStepNum !== 10 &&
      selectedAccountTypeId
    ) {
      window.addEventListener('beforeunload', handleUnload);
    }
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [
    selectedProvider,
    isCustomerWebView,
    currentStepNum,
    selectedAccountTypeId,
    transferState,
    sendManualSalesforceRequestPayload,
    sendYodleeSalesforceTransferPayload,
  ]);

  const steps = getTransferFlowSteps(dispatch, transferState, sendAssetTransferPayload);
  const currentStep = steps[currentStepNum as TransferFlowStep];
  const handleCloseExitModal = () => {
    setShowExitModal(false);
  };
  const navigateToLandingPage = () => {
    window.location.assign(transferState.cancelUrl);
    setShowExitModal(false);
  };
  return (
    <main>
      <section className="layout-section">
        <StepHeader text={currentStep.header} />
        <ExitModal
          open={showExitModal}
          onClose={handleCloseExitModal}
          onCancel={navigateToLandingPage}
        />
        {currentStep.component}
      </section>
      <StepFooter footerButtons={currentStep.footer} isLoading={isLoading} />
    </main>
  );
};

export default TransferFlow;
